/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import Swal from 'sweetalert2';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDSnackbar from 'components/MDSnackbar';
import MDInput from 'components/MDInput';
import MDSelect from 'components/MDSelect';
import TabPanel from 'components/TabPanel/TabPanel';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import nopic from 'assets/images/nopic.jpg';
import ProfileUpload from './ProfileUpload';

const initAttr = {
  id: '',
  code: '',
  role_id: '',
  image_id: '',
  branch_id: '',
  prefix_id: '',
  name: '',
  surname: '',
  tel: '',
  email: '',
  user_name: '',
  idcard: '',
  user_pass: '',
  user_pass_again: '',
  address: '',
  province_id: '',
  district_id: '',
  subdistrict_id: '',
  zipcode: '',
  image: nopic,
  enable: 'Y',
  created_at: '',
  updated_at: '',
};

function ProfileEdit() {
  const mod = 'profile';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cboProvince, setCboProvince] = useState([]);
  const [cboDistrict, setCboDistrict] = useState([]);
  const [cboSubDistrict, setCboSubDistrict] = useState([]);
  const [cboPrefix, setCboPrefix] = useState([]);
  const [tab, setTab] = useState(0);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const district = (province_id) => {
    setCboDistrict([]);
    setCboSubDistrict([]);
    FetchData('OPTIONS', `/${mod}/district`, app.token, {
      lang: app.lang,
      province_id: province_id,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboDistrict(result.district);
      } else {
        Func.exitApp();
      }
    });
  };

  const subDistrict = (district_id) => {
    setCboSubDistrict([]);
    FetchData('OPTIONS', `/${mod}/subdistrict`, app.token, {
      lang: app.lang,
      district_id: district_id,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboSubDistrict(result.subdistrict);
      } else {
        Func.exitApp();
      }
    });
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.name, 1)) return;

    setConfirm(true);
  };

  const open = () => {
    setLoading(true);
    setConfirm(false);

    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboPrefix(result.prefix);
        setCboProvince(result.province);
        district(result.data.province_id, result.data.district_id);
        subDistrict(result.data.district_id);
        setAttr({
          ...result.data,
          user_pass: '',
          user_pass_again: '',
          tel: Func.maskTel(result.data.tel),
        });

        setLoading(false);
      }
    });
  };

  const edit = () => {
    setLoading(true);
    setConfirm(false);

    FetchData('PATCH', `/${mod}`, app.token, {
      prefix_id: attr.prefix_id,
      name: attr.name,
      surname: attr.surname,
      idcard: attr.idcard,
      tel: Func.formatTel(attr.tel),
      branch_id: attr.branch_id,
      address: attr.address,
      email: attr.email,
      province_id: attr.province_id,
      subdistrict_id: attr.subdistrict_id,
      district_id: attr.district_id,
      zipcode: attr.zipcode,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const upload = (filedata) => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        FetchData('OPTIONS', `/${mod}/upload`, app.token, {
          filedata: filedata,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            setAttr({
              ...attr,
              image: result.url,
            });

            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderImage = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        <MDTypography variant='h5' mb={3}>
          Avatar
        </MDTypography>
        <MDBox>
          {attr.image === '' ? null : (
            <MDBox component='img' src={attr.image} alt={attr.name} width='100%' height='100%' />
          )}
          <MDBox mr={1} display='flex' justifyContent='center'>
            <ProfileUpload uploadData={upload} />
          </MDBox>
        </MDBox>
      </Paper>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <MDSelect
            label='คำนำหน้า'
            labelDefault={t(`element.please_select`)}
            data={cboPrefix}
            value={attr.prefix_id}
            onChange={(e) => setAttr({ ...attr, prefix_id: e.target.value })}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MDInput
            label='ชื่อ *'
            onChange={(e) => setAttr({ ...attr, name: e.target.value })}
            value={attr.name}
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MDInput
            label='นามสกุล'
            onChange={(e) => setAttr({ ...attr, surname: e.target.value })}
            value={attr.surname}
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <MDInput
            label={`${t(`attr.idcard`)}`}
            onChange={(e) => setAttr({ ...attr, idcard: Func.maskIdCard(e.target.value) })}
            value={attr.idcard}
            variant='standard'
            fullWidth
            type='search'
            inputProps={{
              autoComplete: 'off',
              maxLength: 17,
              placeholder: '0-0000-00000-00-0',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label={`${t(`attr.tel`)}`}
            onChange={(e) => setAttr({ ...attr, tel: Func.maskTel(e.target.value) })}
            value={attr.tel}
            variant='standard'
            fullWidth
            type='search'
            inputProps={{
              autoComplete: 'off',
              maxLength: 14,
              placeholder: '(000) 000 0000',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label={`${t(`attr.email`)}`}
            onChange={(e) => setAttr({ ...attr, email: e.target.value })}
            value={attr.email}
            type='email'
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDInput
            label={`${t(`attr.address`)}`}
            onChange={(e) => setAttr({ ...attr, address: e.target.value })}
            value={attr.address}
            variant='standard'
            multiline
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDSelect
            label='จังหวัด'
            labelDefault={t(`element.please_select`)}
            data={cboProvince}
            value={attr.province_id}
            onChange={(e) => {
              setAttr({ ...attr, province_id: e.target.value, zipcode: '' });
              district(e.target.value);
            }}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDSelect
            label='อำเภอ / เขต'
            labelDefault={t(`element.please_select`)}
            data={cboDistrict}
            value={attr.district_id}
            onChange={(e) => {
              setAttr({ ...attr, district_id: e.target.value, zipcode: '' });
              subDistrict(e.target.value);
            }}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDSelect
            label='ตำบล / แขวง'
            labelDefault={t(`element.please_select`)}
            data={cboSubDistrict}
            value={attr.subdistrict_id}
            onChange={(e) => {
              cboSubDistrict.forEach((item, i) => {
                if (item.id === e.target.value) {
                  setAttr({ ...attr, subdistrict_id: e.target.value, zipcode: item.zip_code });
                }
              });
            }}
            inputProps={{
              autoComplete: 'off',
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDInput
            label='รหัสไปรษณีย์'
            onChange={(e) => setAttr({ ...attr, zipcode: e.target.value })}
            value={attr.zipcode}
            type='number'
            variant='standard'
            inputProps={{
              autoComplete: 'off',
            }}
            fullWidth
          />
        </Grid>
      </Grid>
    );
  };

  const renderInfoEdit = () => {
    return (
      <Paper variant='outlined' sx={{ padding: 2, marginBottom: 2 }}>
        {renderInfo()}

        {confirm ? (
          <Grid container mt={3}>
            <Grid item xs={6}>
              <MDButton variant='outlined' color='error' onClick={() => setConfirm(false)}>
                <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
              </MDButton>
            </Grid>
            <Grid item xs={6} display='flex' justifyContent='flex-end'>
              <MDButton variant='gradient' color='success' onClick={edit}>
                <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
              </MDButton>
            </Grid>
          </Grid>
        ) : (
          <Grid container mt={3}>
            <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
              <MDButton variant='outlined' color='success' onClick={editConfirm}>
                <Icon>save</Icon>&nbsp;{t(`element.save`)}
              </MDButton>
            </Grid>
          </Grid>
        )}
      </Paper>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12}>
            <Card sx={{ p: 2 }}>
              <MDBox sx={{ width: '100%' }}>
                <MDBox>
                  <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label='tabs-addedit'>
                    <Tab label={t(`element.info`)} />
                    <Tab label={t(`attr.image`)} />
                  </Tabs>
                </MDBox>
                <TabPanel value={tab} index={0}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={8} lg={6} xl={5}>
                      {renderInfoEdit()}
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={1}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={5} lg={3}>
                      {renderImage()}
                    </Grid>
                  </Grid>
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    open();
  }, []);

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.profile`), t(`menu.profile`)]} />
      {renderOpen()}
      <Footer />
      {renderLoading()}
      {notify}
    </DashboardLayout>
  );
}

export default ProfileEdit;
